import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ScalesService } from '../../../../../../../services/API/scales/scales.service';
import { AuxiliarService } from '../../../../../../../services/auxiliar.service';
import { io, Socket } from 'socket.io-client';

type CropRect = { x: number; y: number; w: number; h: number };

@Component({
  selector: 'app-scale-cropper',
  templateUrl: './scale-cropper.component.html',
  styleUrls: ['./scale-cropper.component.css'],
  standalone: false,
})
export class ScaleCropperComponent implements OnInit {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  loading = true;
  saving = false;
  capturing = false;
  error = '';

  imageDataUrl: string | null = null;
  private img = new Image();
  private selecting = false;
  private selectStart: { x: number; y: number } | null = null;
  private socket: Socket | null = null;
  private captureToken = '';

  // Sin recorte guardado: canvas vacío hasta que el usuario dibuje.
  rect: CropRect = { x: 0, y: 0, w: 0, h: 0 };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { scaleId: number; current?: CropRect | null },
    private dialogRef: MatDialogRef<ScaleCropperComponent>,
    private scalesService: ScalesService,
    private auxiliarService: AuxiliarService,
  ) {}

  ngOnInit(): void {
    if (this.data.current) {
      this.rect = { ...this.data.current };
    }

    this.scalesService.getCropSample(this.data.scaleId).subscribe({
      next: (res) => {
        this.imageDataUrl = res?.image || null;
        this.loading = false;
        if (this.imageDataUrl) {
          this.loadImage(this.imageDataUrl);
        } else {
          this.drawEmpty();
        }
      },
      error: () => {
        this.loading = false;
        this.error = 'No se pudo cargar la imagen de referencia.';
        this.drawEmpty();
      },
    });

    // Dedicated socket connection (admin/settings context).
    this.socket = io(this.auxiliarService.getSocket(), {
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
  }

  close(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.dialogRef.close(null);
  }

  capture(): void {
    if (!this.socket?.connected) {
      this.error = 'Socket no conectado. Comprueba la URL del socket.';
      return;
    }
    this.error = '';
    this.capturing = true;
    this.captureToken = this.makeToken(10);
    const scale = String(this.data.scaleId);

    const onResponse = (msg: any) => {
      if (!msg || String(msg['idToken'] || '') !== this.captureToken) {
        return;
      }
      const msgScale = String(msg?.scale ?? msg?.scale_id ?? '');
      if (msgScale && msgScale !== scale) {
        return;
      }
      cleanup();
      const imageName = String(msg['imageName'] || '').trim();
      if (!imageName) {
        this.capturing = false;
        this.error = 'No se recibió el nombre de la imagen.';
        return;
      }
      this.scalesService.getCropImageByName(imageName).subscribe({
        next: (res) => {
          this.capturing = false;
          const dataUrl = res?.image || null;
          if (!dataUrl) {
            this.error = 'No se pudo cargar la imagen capturada.';
            return;
          }
          this.imageDataUrl = dataUrl;
          this.loadImage(dataUrl);
        },
        error: () => {
          this.capturing = false;
          this.error = 'No se pudo cargar la imagen capturada.';
        },
      });
    };

    const onError = (msg: any) => {
      if (!msg || String(msg['idToken'] || '') !== this.captureToken) {
        return;
      }
      cleanup();
      this.capturing = false;
      const detail = String(msg['message'] || '').trim();
      this.error = detail || 'No se pudo capturar la foto desde la báscula.';
    };

    const cleanup = () => {
      this.socket?.off('captureImageResponse', onResponse);
      this.socket?.off('captureImageError', onError);
    };

    this.socket.on('captureImageResponse', onResponse);
    this.socket.on('captureImageError', onError);
    this.socket.emit('joinScale', { scale });
    this.socket.emit('captureImage', { idToken: this.captureToken, scale });

    setTimeout(() => {
      if (!this.capturing) return;
      cleanup();
      this.capturing = false;
      this.error = 'Timeout capturando la foto (20s).';
    }, 20000);
  }

  save(): void {
    if (this.rect.w <= 0 || this.rect.h <= 0) {
      this.error = 'Selecciona una zona de recorte antes de guardar.';
      return;
    }
    const rect = this.normalizeRect(this.rect);
    if (!this.imageDataUrl) {
      this.dialogRef.close({ rect, uploaded: false });
      return;
    }

    this.saving = true;
    this.error = '';
    const preview = this.canvasRef.nativeElement.toDataURL('image/jpeg', 0.85);

    this.scalesService.uploadCropSample(this.data.scaleId, preview).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close({ rect, uploaded: true });
      },
      error: () => {
        // El recorte es lo importante; la imagen de referencia es opcional.
        this.saving = false;
        this.dialogRef.close({ rect, uploaded: false });
      },
    });
  }

  // Canvas interactions: click + drag to draw a new selection (resets previous).
  onMouseDown(ev: MouseEvent): void {
    if (!this.imageDataUrl) {
      return;
    }
    this.selecting = true;
    this.selectStart = this.getCanvasPoint(ev);
    const canvas = this.canvasRef.nativeElement;
    this.rect = {
      x: this.selectStart.x / canvas.width,
      y: this.selectStart.y / canvas.height,
      w: 0,
      h: 0,
    };
    this.redraw();
  }

  onMouseMove(ev: MouseEvent): void {
    if (!this.selecting || !this.selectStart || !this.imageDataUrl) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const point = this.getCanvasPoint(ev);
    const left = Math.min(this.selectStart.x, point.x);
    const top = Math.min(this.selectStart.y, point.y);
    const width = Math.abs(point.x - this.selectStart.x);
    const height = Math.abs(point.y - this.selectStart.y);

    this.rect = {
      x: left / canvas.width,
      y: top / canvas.height,
      w: width / canvas.width,
      h: height / canvas.height,
    };
    this.redraw();
  }

  onMouseUp(): void {
    if (!this.selecting) {
      return;
    }
    this.selecting = false;
    this.selectStart = null;
    this.rect = this.normalizeRect(this.rect);
    this.redraw();
  }

  onMouseLeave(): void {
    this.onMouseUp();
  }

  private getCanvasPoint(ev: MouseEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;
    const bounds = canvas.getBoundingClientRect();
    const scaleX = canvas.width / bounds.width;
    const scaleY = canvas.height / bounds.height;

    return {
      x: (ev.clientX - bounds.left) * scaleX,
      y: (ev.clientY - bounds.top) * scaleY,
    };
  }

  private loadImage(dataUrl: string): void {
    this.img.onload = () => this.redraw();
    this.img.src = dataUrl;
  }

  private redraw(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!this.imageDataUrl) {
      this.drawEmpty();
      return;
    }

    ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const r = this.rect;
    const rx = r.x * canvas.width;
    const ry = r.y * canvas.height;
    const rw = r.w * canvas.width;
    const rh = r.h * canvas.height;

    if (rw > 0 && rh > 0) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(rx, ry, rw, rh);
      ctx.clip();
      ctx.drawImage(this.img, 0, 0, canvas.width, canvas.height);
      ctx.restore();

      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2;
      ctx.strokeRect(rx, ry, rw, rh);
    }
  }

  private drawEmpty(): void {
    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  private normalizeRect(r: CropRect): CropRect {
    const x = Math.max(0, Math.min(1, r.x));
    const y = Math.max(0, Math.min(1, r.y));
    const w = Math.max(0.05, Math.min(1, r.w));
    const h = Math.max(0.05, Math.min(1, r.h));
    // Clamp to bounds
    const nx = Math.max(0, Math.min(1 - w, x));
    const ny = Math.max(0, Math.min(1 - h, y));
    return { x: nx, y: ny, w, h };
  }

  private makeToken(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let out = '';
    for (let i = 0; i < length; i++) {
      out += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return out;
  }
}

